import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatDate } from '../../lib/utils';
import {
    Plus, QrCode, Copy, Check, Ticket, Calendar, Search, X, Download,
    Send, Loader2, Shield, ShieldOff, Trash2, Building2, User, Package,
    Eye, ChevronDown, ChevronUp, MonitorSmartphone, Users, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCodes() {
    const [codes, setCodes] = useState([]);
    const [stats, setStats] = useState({});
    const [attractions, setAttractions] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedCode, setCopiedCode] = useState(null);

    // Dialogs
    const [singleOpen, setSingleOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [generating, setGenerating] = useState(false);

    // Single code form
    const [singleForm, setSingleForm] = useState({
        attraction_ids: [],
        customer_name: '',
        customer_email: '',
        max_uses: 2,
        expiration_days: 365
    });

    // Bulk code form
    const [bulkForm, setBulkForm] = useState({
        attraction_ids: [],
        assigned_operator_id: '',
        quantity: 10,
        max_uses: 2,
        expiration_days: 365,
        label: ''
    });

    const fetchAll = useCallback(async () => {
        try {
            const [codesRes, statsRes, opsRes] = await Promise.all([
                axios.get(`${API}/api/admin/codes`, { withCredentials: true }),
                axios.get(`${API}/api/admin/codes/stats`, { withCredentials: true }),
                axios.get(`${API}/api/admin/operators`, { withCredentials: true })
            ]);
            setCodes(codesRes.data);
            setStats(statsRes.data);
            setOperators(opsRes.data);
        } catch (err) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAttractions = async () => {
        try {
            const res = await axios.get(`${API}/api/public/cities`, { withCredentials: true });
            const allAttr = [];
            for (const city of res.data) {
                const aRes = await axios.get(`${API}/api/public/cities/${city.id}/attractions`, { withCredentials: true });
                allAttr.push(...aRes.data.map(a => ({ ...a, city_name: city.name })));
            }
            setAttractions(allAttr);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => { fetchAll(); fetchAttractions(); }, [fetchAll]);

    const handleGenerateSingle = async (e) => {
        e.preventDefault();
        if (!singleForm.attraction_ids.length) return toast.error('Select an attraction');
        setGenerating(true);
        try {
            const res = await axios.post(`${API}/api/admin/codes/generate`, {
                ...singleForm,
                quantity: 1,
                source: 'admin_manual'
            }, { withCredentials: true });
            toast.success(`Code created: ${res.data.codes[0].code}`);
            setSingleOpen(false);
            setSingleForm({ attraction_ids: [], customer_name: '', customer_email: '', max_uses: 2, expiration_days: 365 });
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create code');
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateBulk = async (e) => {
        e.preventDefault();
        if (!bulkForm.attraction_ids.length) return toast.error('Select an attraction');
        if (!bulkForm.assigned_operator_id) return toast.error('Select an operator');
        setGenerating(true);
        try {
            const res = await axios.post(`${API}/api/admin/codes/generate`, {
                ...bulkForm,
                source: 'admin_bulk'
            }, { withCredentials: true });
            toast.success(`Generated ${res.data.count} codes for operator`);
            setBulkOpen(false);
            setBulkForm({ attraction_ids: [], assigned_operator_id: '', quantity: 10, max_uses: 2, expiration_days: 365, label: '' });
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate codes');
        } finally {
            setGenerating(false);
        }
    };

    const toggleCodeActive = async (code) => {
        try {
            await axios.put(`${API}/api/admin/codes/${code.id}`, {}, { withCredentials: true });
            toast.success(code.is_active ? 'Code revoked' : 'Code reactivated');
            fetchAll();
        } catch (err) {
            toast.error('Failed to update code');
        }
    };

    const deleteCode = async (code) => {
        if (!window.confirm(`Delete code ${code.code}? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/api/admin/codes/${code.id}`, { withCredentials: true });
            toast.success('Code deleted');
            fetchAll();
        } catch (err) {
            toast.error('Failed to delete code');
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
        toast.success('Code copied!');
    };

    const openQr = (code) => { setSelectedCode(code); setQrOpen(true); };
    const openDetail = (code) => { setSelectedCode(code); setDetailOpen(true); };

    const downloadQr = () => {
        if (!selectedCode) return;
        const link = document.createElement('a');
        link.href = `${API}/api/qr/${selectedCode.code}`;
        link.download = `qr-${selectedCode.code}.png`;
        link.click();
    };

    const getStatusBadge = (code) => {
        if (!code.is_active) return { text: 'Revoked', cls: 'bg-gray-500/10 text-gray-400' };
        if (code.current_uses >= code.max_uses) return { text: 'Fully Used', cls: 'bg-amber-500/10 text-amber-400' };
        if (new Date(code.expires_at) < new Date()) return { text: 'Expired', cls: 'bg-red-500/10 text-red-400' };
        if (code.current_uses > 0) return { text: `${code.current_uses}/${code.max_uses} Used`, cls: 'bg-blue-500/10 text-blue-400' };
        return { text: 'Active', cls: 'bg-emerald-500/10 text-emerald-400' };
    };

    const getSourceLabel = (code) => {
        if (code.source === 'admin_manual') return { text: 'Direct', icon: User, cls: 'text-gold' };
        if (code.source === 'admin_bulk') return { text: 'Bulk', icon: Package, cls: 'text-blue-400' };
        if (code.source === 'auto_purchase') return { text: 'Purchase', icon: BarChart3, cls: 'text-emerald-400' };
        return { text: 'Operator', icon: Building2, cls: 'text-muted-foreground' };
    };

    // Filtering
    const filtered = codes.filter(c => {
        if (filter === 'active' && (c.current_uses >= c.max_uses || !c.is_active)) return false;
        if (filter === 'used' && c.current_uses < c.max_uses) return false;
        if (filter === 'revoked' && c.is_active) return false;
        if (sourceFilter === 'admin' && !['admin_manual', 'admin_bulk'].includes(c.source)) return false;
        if (sourceFilter === 'operator' && ['admin_manual', 'admin_bulk', 'auto_purchase'].includes(c.source)) return false;
        if (sourceFilter === 'bulk' && c.source !== 'admin_bulk') return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return c.code.toLowerCase().includes(q) ||
                (c.customer_name || '').toLowerCase().includes(q) ||
                (c.customer_email || '').toLowerCase().includes(q) ||
                (c.operator_name || '').toLowerCase().includes(q) ||
                (c.label || '').toLowerCase().includes(q);
        }
        return true;
    });

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-codes">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Codes & Vouchers</h1>
                        <p className="text-muted-foreground mt-1">Create, manage, and track all QR codes and vouchers</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={async () => {
                                try {
                                    const res = await axios.get(`${API}/api/admin/codes/export-pdf`, {
                                        withCredentials: true,
                                        responseType: 'blob'
                                    });
                                    const url = URL.createObjectURL(res.data);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'QR_Codes.pdf';
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast.success('QR PDF downloaded');
                                } catch (e) {
                                    toast.error('No codes to export');
                                }
                            }}
                            variant="outline"
                            className="border-white/10 text-muted-foreground hover:text-white gap-2"
                            data-testid="admin-export-pdf-btn"
                        >
                            <Download className="w-4 h-4" /> Export QR PDF
                        </Button>
                        <Button
                            onClick={() => setSingleOpen(true)}
                            variant="outline"
                            className="border-gold/30 text-gold hover:bg-gold/10 gap-2"
                            data-testid="create-single-code-btn"
                        >
                            <User className="w-4 h-4" /> Single Code
                        </Button>
                        <Button
                            onClick={() => setBulkOpen(true)}
                            className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                            data-testid="create-bulk-codes-btn"
                        >
                            <Package className="w-4 h-4" /> Bulk for Operator
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { title: 'Total Codes', value: stats.total || 0, icon: Ticket, color: 'text-white' },
                        { title: 'Active', value: stats.active || 0, icon: Shield, color: 'text-emerald-400' },
                        { title: 'Fully Used', value: stats.used || 0, icon: Check, color: 'text-amber-400' },
                        { title: 'Redemptions', value: stats.total_redemptions || 0, icon: Users, color: 'text-blue-400' }
                    ].map((s, i) => (
                        <Card key={s.title} className="bg-[#121620] border-white/10">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-muted-foreground">{s.title}</p>
                                        <p className={`text-2xl font-bold font-outfit ${s.color}`}>{s.value}</p>
                                    </div>
                                    <s.icon className={`w-8 h-8 ${s.color} opacity-30`} />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <Tabs value={filter} onValueChange={setFilter} className="flex-1">
                        <TabsList className="bg-[#121620] border border-white/10">
                            <TabsTrigger value="all" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">All</TabsTrigger>
                            <TabsTrigger value="active" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Active</TabsTrigger>
                            <TabsTrigger value="used" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Used</TabsTrigger>
                            <TabsTrigger value="revoked" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Revoked</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <div className="flex items-center gap-3">
                        <Select value={sourceFilter} onValueChange={setSourceFilter}>
                            <SelectTrigger className="w-[160px] bg-[#121620] border-white/10" data-testid="source-filter">
                                <SelectValue placeholder="Source" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121620] border-white/10">
                                <SelectItem value="all">All Sources</SelectItem>
                                <SelectItem value="admin">Admin Created</SelectItem>
                                <SelectItem value="operator">Operator</SelectItem>
                                <SelectItem value="bulk">Bulk Batches</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search code, name, email..."
                                className="pl-10 bg-[#121620] border-white/10"
                                data-testid="code-search-input"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <X className="w-4 h-4 text-muted-foreground hover:text-white" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Codes Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Code</TableHead>
                                    <TableHead className="text-muted-foreground">Source</TableHead>
                                    <TableHead className="text-muted-foreground">Attraction</TableHead>
                                    <TableHead className="text-muted-foreground">Assigned To</TableHead>
                                    <TableHead className="text-muted-foreground">Uses</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Expires</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((code) => {
                                    const status = getStatusBadge(code);
                                    const source = getSourceLabel(code);
                                    const SourceIcon = source.icon;
                                    return (
                                        <TableRow key={code.id} className="border-white/10 hover:bg-white/5">
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => openQr(code)} className="w-8 h-8 rounded bg-gold/10 flex items-center justify-center hover:bg-gold/20 transition-colors shrink-0">
                                                        <QrCode className="w-4 h-4 text-gold" />
                                                    </button>
                                                    <div>
                                                        <code className="font-mono text-white text-sm">{code.code}</code>
                                                        {code.label && <p className="text-[10px] text-muted-foreground">{code.label}</p>}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className={`flex items-center gap-1 text-xs ${source.cls}`}>
                                                    <SourceIcon className="w-3 h-3" />
                                                    <span>{source.text}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-white truncate block max-w-[150px]">
                                                    {(code.attraction_names || []).join(', ') || '—'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {code.customer_name ? (
                                                    <div>
                                                        <p className="text-sm text-white">{code.customer_name}</p>
                                                        <p className="text-[10px] text-muted-foreground">{code.customer_email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">{code.operator_name}</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-sm">
                                                    <MonitorSmartphone className="w-3 h-3 text-muted-foreground" />
                                                    <span className="text-white">{code.current_uses}</span>
                                                    <span className="text-muted-foreground">/ {code.max_uses}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded-full text-xs ${status.cls}`}>{status.text}</span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-muted-foreground text-xs">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(code.expires_at)}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-0.5">
                                                    <Button variant="ghost" size="sm" onClick={() => copyCode(code.code)} title="Copy code" className="text-muted-foreground hover:text-white h-8 w-8 p-0">
                                                        {copiedCode === code.code ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openDetail(code)} title="Details" className="text-muted-foreground hover:text-white h-8 w-8 p-0">
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => toggleCodeActive(code)} title={code.is_active ? 'Revoke' : 'Reactivate'} className="text-muted-foreground hover:text-amber-400 h-8 w-8 p-0">
                                                        {code.is_active ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => deleteCode(code)} title="Delete" className="text-muted-foreground hover:text-red-400 h-8 w-8 p-0">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            {searchQuery ? 'No codes match your search.' : 'No codes found. Create your first code.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* ========== SINGLE CODE DIALOG ========== */}
                <Dialog open={singleOpen} onOpenChange={setSingleOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <User className="w-5 h-5 text-gold" />
                                Create Code for Customer
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                Generate a single QR code / voucher for a direct customer. Valid on 2 devices, expires in 1 year.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleGenerateSingle} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label>Audio Attraction</Label>
                                <Select
                                    value={singleForm.attraction_ids[0] || ''}
                                    onValueChange={(v) => setSingleForm({ ...singleForm, attraction_ids: [v] })}
                                >
                                    <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="single-select-attraction">
                                        <SelectValue placeholder="Choose attraction" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121620] border-white/10 max-h-60">
                                        {attractions.map(a => (
                                            <SelectItem key={a.id} value={a.id}>{a.title} ({a.city_name})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Customer Name</Label>
                                    <Input
                                        value={singleForm.customer_name}
                                        onChange={(e) => setSingleForm({ ...singleForm, customer_name: e.target.value })}
                                        placeholder="John Doe"
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="single-customer-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Customer Email</Label>
                                    <Input
                                        type="email"
                                        value={singleForm.customer_email}
                                        onChange={(e) => setSingleForm({ ...singleForm, customer_email: e.target.value })}
                                        placeholder="john@example.com"
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="single-customer-email"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1">
                                        <MonitorSmartphone className="w-3 h-3" /> Max Devices
                                    </Label>
                                    <Input
                                        type="number"
                                        value={singleForm.max_uses}
                                        onChange={(e) => setSingleForm({ ...singleForm, max_uses: parseInt(e.target.value) || 2 })}
                                        min={1} max={10}
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="single-max-uses"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" /> Valid Days
                                    </Label>
                                    <Input
                                        type="number"
                                        value={singleForm.expiration_days}
                                        onChange={(e) => setSingleForm({ ...singleForm, expiration_days: parseInt(e.target.value) || 365 })}
                                        min={1}
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                            </div>
                            <div className="p-3 rounded-md bg-gold/5 border border-gold/20 text-xs text-muted-foreground">
                                The traveler can use this code on <strong className="text-gold">{singleForm.max_uses} devices</strong>.
                                Audio access will be valid for <strong className="text-gold">1 year from redemption date</strong>.
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button type="button" variant="outline" onClick={() => setSingleOpen(false)} className="border-white/10">Cancel</Button>
                                <Button type="submit" disabled={generating} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="single-generate-btn">
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                                    Create Code
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* ========== BULK CODES DIALOG ========== */}
                <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <Package className="w-5 h-5 text-blue-400" />
                                Bulk Codes for Operator / Venue
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                Generate a batch of codes and assign to an operator or venue to distribute to their clients.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleGenerateBulk} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label>Assign to Operator / Venue</Label>
                                <Select
                                    value={bulkForm.assigned_operator_id}
                                    onValueChange={(v) => setBulkForm({ ...bulkForm, assigned_operator_id: v })}
                                >
                                    <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="bulk-select-operator">
                                        <SelectValue placeholder="Select operator" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121620] border-white/10 max-h-60">
                                        {operators.map(op => (
                                            <SelectItem key={op.id} value={op.id}>
                                                {op.company_name} ({op.email || 'No email'})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Audio Attraction</Label>
                                <Select
                                    value={bulkForm.attraction_ids[0] || ''}
                                    onValueChange={(v) => setBulkForm({ ...bulkForm, attraction_ids: [v] })}
                                >
                                    <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="bulk-select-attraction">
                                        <SelectValue placeholder="Choose attraction" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121620] border-white/10 max-h-60">
                                        {attractions.map(a => (
                                            <SelectItem key={a.id} value={a.id}>{a.title} ({a.city_name})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Quantity</Label>
                                    <Input
                                        type="number"
                                        value={bulkForm.quantity}
                                        onChange={(e) => setBulkForm({ ...bulkForm, quantity: parseInt(e.target.value) || 10 })}
                                        min={1} max={500}
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="bulk-quantity"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max Devices</Label>
                                    <Input
                                        type="number"
                                        value={bulkForm.max_uses}
                                        onChange={(e) => setBulkForm({ ...bulkForm, max_uses: parseInt(e.target.value) || 2 })}
                                        min={1} max={10}
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Valid Days</Label>
                                    <Input
                                        type="number"
                                        value={bulkForm.expiration_days}
                                        onChange={(e) => setBulkForm({ ...bulkForm, expiration_days: parseInt(e.target.value) || 365 })}
                                        min={1}
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Batch Label (optional)</Label>
                                <Input
                                    value={bulkForm.label}
                                    onChange={(e) => setBulkForm({ ...bulkForm, label: e.target.value })}
                                    placeholder="e.g. Summer 2026 Promo, Hotel Partnership"
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="bulk-label"
                                />
                            </div>
                            <div className="p-3 rounded-md bg-blue-500/5 border border-blue-500/20 text-xs text-muted-foreground">
                                This will create <strong className="text-blue-400">{bulkForm.quantity} codes</strong> assigned to the selected operator.
                                The operator can distribute these to their clients via email/SMS.
                                Each code works on <strong className="text-blue-400">{bulkForm.max_uses} devices</strong>.
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button type="button" variant="outline" onClick={() => setBulkOpen(false)} className="border-white/10">Cancel</Button>
                                <Button type="submit" disabled={generating} className="bg-blue-500 hover:bg-blue-600 text-white gap-2" data-testid="bulk-generate-btn">
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                                    Generate {bulkForm.quantity} Codes
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* ========== QR CODE DIALOG ========== */}
                <Dialog open={qrOpen} onOpenChange={setQrOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-sm">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit text-center">QR Code</DialogTitle>
                            <DialogDescription className="text-center text-muted-foreground text-sm">Scan with the traveler app to redeem</DialogDescription>
                        </DialogHeader>
                        {selectedCode && (
                            <div className="flex flex-col items-center py-4">
                                <div className="bg-white p-4 rounded-lg">
                                    <img src={`${API}/api/qr/${selectedCode.code}`} alt="QR" className="w-52 h-52" />
                                </div>
                                <p className="mt-4 font-mono text-xl text-gold tracking-wider">{selectedCode.code}</p>
                                {selectedCode.customer_name && (
                                    <p className="text-xs text-muted-foreground mt-1">For: {selectedCode.customer_name}</p>
                                )}
                                <div className="flex gap-3 mt-4">
                                    <Button onClick={downloadQr} variant="outline" className="border-white/10 gap-2">
                                        <Download className="w-4 h-4" /> Download
                                    </Button>
                                    <Button onClick={() => copyCode(selectedCode.code)} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2">
                                        <Copy className="w-4 h-4" /> Copy Code
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* ========== DETAIL DIALOG ========== */}
                <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit">Code Details</DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">Full information about this code</DialogDescription>
                        </DialogHeader>
                        {selectedCode && (
                            <div className="space-y-4 mt-2">
                                <div className="p-4 rounded-md bg-[#0A0D14] border border-white/10 text-center">
                                    <p className="font-mono text-2xl text-gold tracking-wider">{selectedCode.code}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">Source</p>
                                        <p className="text-white capitalize">{(selectedCode.source || 'operator').replace('_', ' ')}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">Status</p>
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(selectedCode).cls}`}>
                                            {getStatusBadge(selectedCode).text}
                                        </span>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">Uses</p>
                                        <p className="text-white">{selectedCode.current_uses} / {selectedCode.max_uses} devices</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">Expires</p>
                                        <p className="text-white">{formatDate(selectedCode.expires_at)}</p>
                                    </div>
                                    {selectedCode.customer_name && (
                                        <div className="space-y-1">
                                            <p className="text-muted-foreground text-xs">Customer</p>
                                            <p className="text-white">{selectedCode.customer_name}</p>
                                        </div>
                                    )}
                                    {selectedCode.customer_email && (
                                        <div className="space-y-1">
                                            <p className="text-muted-foreground text-xs">Email</p>
                                            <p className="text-white">{selectedCode.customer_email}</p>
                                        </div>
                                    )}
                                    <div className="col-span-2 space-y-1">
                                        <p className="text-muted-foreground text-xs">Attractions</p>
                                        <p className="text-white">{(selectedCode.attraction_names || []).join(', ')}</p>
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        <p className="text-muted-foreground text-xs">Operator / Owner</p>
                                        <p className="text-white">{selectedCode.operator_name}</p>
                                    </div>
                                    {selectedCode.label && (
                                        <div className="col-span-2 space-y-1">
                                            <p className="text-muted-foreground text-xs">Batch Label</p>
                                            <p className="text-white">{selectedCode.label}</p>
                                        </div>
                                    )}
                                    <div className="col-span-2 space-y-1">
                                        <p className="text-muted-foreground text-xs">Created</p>
                                        <p className="text-white">{formatDate(selectedCode.created_at)}</p>
                                    </div>
                                </div>

                                {/* Redemption History */}
                                {selectedCode.redemptions?.length > 0 && (
                                    <div className="space-y-2">
                                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Redemption History</p>
                                        <div className="space-y-2">
                                            {selectedCode.redemptions.map((r, i) => (
                                                <div key={r.user_email + '-' + (r.redeemed_at || i)} className="flex items-center justify-between p-2 rounded bg-[#0A0D14] border border-white/5 text-sm">
                                                    <div>
                                                        <p className="text-white">{r.user_email}</p>
                                                        {r.user_name && <p className="text-xs text-muted-foreground">{r.user_name}</p>}
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">{formatDate(r.redeemed_at)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
